<template>
  <div class="register-form">
    <!-- Error Alert -->
    <div v-if="hasError" class="alert alert-danger" role="alert">
      <i class="fi fi-alert"></i>
      {{ errorMessage }}
    </div>

    <!-- Success Alert -->
    <div
      v-if="successMessage && !isSettingUp"
      class="alert alert-success"
      role="alert"
    >
      <i class="fi fi-check"></i>
      {{ successMessage }}
    </div>

    <!-- Workspace initialisation overlay is now handled by AuthLayout -->

    <div v-if="!isSettingUp">
      <form @submit.prevent="handleRegister" class="auth-form">
        <div class="form-row">
          <div class="form-group">
            <input
              id="first_name"
              v-model="userData.first_name"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': errors.first_name }"
              placeholder="First Name"
              required
              :disabled="isLoading"
            />
            <div v-if="errors.first_name" class="invalid-feedback">
              {{ errors.first_name[0] }}
            </div>
          </div>

          <div class="form-group">
            <input
              id="last_name"
              v-model="userData.last_name"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': errors.last_name }"
              placeholder="Last Name"
              required
              :disabled="isLoading"
            />
            <div v-if="errors.last_name" class="invalid-feedback">
              {{ errors.last_name[0] }}
            </div>
          </div>
        </div>

        <div class="form-group">
          <input
            id="hotel_name"
            v-model="userData.hotel_name"
            type="text"
            class="form-control"
            :class="{ 'is-invalid': errors.hotel_name }"
            placeholder="Hotel Name"
            required
            :disabled="isLoading"
          />
          <div v-if="errors.hotel_name" class="invalid-feedback">
            {{ errors.hotel_name[0] }}
          </div>
        </div>

        <div class="form-group">
          <input
            id="email"
            v-model="userData.email"
            type="email"
            class="form-control"
            :class="{ 'is-invalid': errors.email }"
            placeholder="Email"
            required
            :disabled="isLoading"
          />
          <div v-if="errors.email" class="invalid-feedback">
            {{ errors.email[0] }}
          </div>
        </div>

        <div class="form-group">
          <div class="password-input-wrapper">
            <input
              id="password"
              v-model="userData.password"
              :type="showPassword ? 'text' : 'password'"
              class="form-control"
              :class="{ 'is-invalid': errors.password }"
              placeholder="Create a password"
              required
              :disabled="isLoading"
            />
            <button
              type="button"
              class="password-toggle"
              @click="showPassword = !showPassword"
              :disabled="isLoading"
            >
              <i :class="showPassword ? 'fi fi-eye-slash' : 'fi fi-eye'"></i>
            </button>
          </div>
          <div v-if="errors.password" class="invalid-feedback">
            {{ errors.password[0] }}
          </div>
          <!-- Password Requirements Checklist -->
          <div
            class="password-requirements"
            :class="{ hidden: allRequirementsMet }"
          >
            <div class="requirement" :class="{ met: passwordMeetsLength }">
              <i
                :class="passwordMeetsLength ? 'fi fi-check' : 'fi fi-circle'"
              ></i>
              <span>At least 8 characters</span>
            </div>
            <div class="requirement" :class="{ met: passwordMeetsUppercase }">
              <i
                :class="passwordMeetsUppercase ? 'fi fi-check' : 'fi fi-circle'"
              ></i>
              <span>At least one uppercase letter</span>
            </div>
            <div class="requirement" :class="{ met: passwordMeetsLowercase }">
              <i
                :class="passwordMeetsLowercase ? 'fi fi-check' : 'fi fi-circle'"
              ></i>
              <span>At least one lowercase letter</span>
            </div>
            <div class="requirement" :class="{ met: passwordMeetsNumber }">
              <i
                :class="passwordMeetsNumber ? 'fi fi-check' : 'fi fi-circle'"
              ></i>
              <span>At least one number</span>
            </div>
            <div class="requirement" :class="{ met: passwordMeetsSpecial }">
              <i
                :class="passwordMeetsSpecial ? 'fi fi-check' : 'fi fi-circle'"
              ></i>
              <span>At least one special character @$!%*?&amp;#</span>
            </div>
          </div>
        </div>

        <div class="form-group">
          <div class="password-input-wrapper">
            <input
              id="password_confirmation"
              v-model="userData.password_confirmation"
              :type="showConfirmPassword ? 'text' : 'password'"
              class="form-control"
              :class="{ 'is-invalid': errors.password_confirmation }"
              placeholder="Confirm your password"
              required
              :disabled="isLoading"
            />
            <button
              type="button"
              class="password-toggle"
              @click="showConfirmPassword = !showConfirmPassword"
              :disabled="isLoading"
            >
              <i
                :class="showConfirmPassword ? 'fi fi-eye-slash' : 'fi fi-eye'"
              ></i>
            </button>
            <div
              v-if="userData.password && userData.password_confirmation"
              class="password-match-indicator"
            >
              <i
                :class="[
                  passwordsMatch ? 'fi fi-check' : 'fi fi-x',
                  passwordsMatch ? 'text-success' : 'text-danger',
                ]"
              ></i>
            </div>
          </div>
          <div v-if="errors.password_confirmation" class="invalid-feedback">
            {{ errors.password_confirmation[0] }}
          </div>
        </div>

        <button
          type="submit"
          class="btn btn-primary btn-block"
          :disabled="isLoading"
        >
          <span v-if="isLoading" class="loading-spinner"></span>
          {{ isLoading ? "Creating Account..." : "Create Account" }}
        </button>
      </form>

      <div class="form-footer">
        <p class="signin-link">
          Already have an account?
          <router-link
            :to="`/${currentLocale}/auth/login`"
            class="link-primary"
          >
            Sign in here
          </router-link>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import AuthUtils from "../../utils/auth.utils";
import axios from "axios";
import API from "@/api";

export default {
  name: "Register",
  data() {
    return {
      userData: {
        first_name: "",
        last_name: "",
        email: "",
        hotel_name: "",
        password: "",
        password_confirmation: "",
      },
      showPassword: false,
      showConfirmPassword: false,
      errors: {},
      successMessage: "",
      isSettingUp: false,
      currentLocale: AuthUtils.getCurrentLocale(),
    };
  },

  computed: {
    ...mapGetters(["isLoading", "hasError", "getError"]),

    errorMessage() {
      const storeError = this.$store.getters.getError;
      if (storeError) return storeError;

      // Handle validation errors
      if (Object.keys(this.errors).length > 0) {
        const firstError = Object.values(this.errors)[0];
        return Array.isArray(firstError) ? firstError[0] : firstError;
      }

      return "";
    },

    // Password requirement checks
    passwordMeetsLength() {
      return this.userData.password.length >= 8;
    },
    passwordMeetsUppercase() {
      return /[A-Z]/.test(this.userData.password);
    },
    passwordMeetsLowercase() {
      return /[a-z]/.test(this.userData.password);
    },
    passwordMeetsNumber() {
      return /\d/.test(this.userData.password);
    },
    passwordMeetsSpecial() {
      return /[@$!%*?&#]/.test(this.userData.password);
    },
    passwordsMatch() {
      return (
        this.userData.password &&
        this.userData.password_confirmation &&
        this.userData.password === this.userData.password_confirmation
      );
    },
    allRequirementsMet() {
      return (
        this.passwordMeetsLength &&
        this.passwordMeetsUppercase &&
        this.passwordMeetsLowercase &&
        this.passwordMeetsNumber &&
        this.passwordMeetsSpecial
      );
    },
  },

  methods: {
    ...mapActions(["apiRegister"]),

    async handleRegister() {
      // Clear previous errors
      this.errors = {};
      this.successMessage = "";

      // Basic validation
      if (!this.validateForm()) {
        return;
      }

      try {
        await this.$store.dispatch("apiRegister", this.userData);
        this.successMessage = "Your account was created! Setting things up…";
        this.isSettingUp = true;
        // Notify parent/layout so it can hide the auth card
        this.$root.$emit("setup-started", true);

        // Poll a lightweight vendor endpoint until it returns 200
        try {
          await this.waitForVendorReady();
          AuthUtils.redirectAfterLogin();
        } catch (e) {
          console.error("Setup polling failed:", e);
          // Still redirect so user can refresh manually; optional
          AuthUtils.redirectAfterLogin();
        }
      } catch (error) {
        console.log("Registration error:", error);
        console.log("Error status:", error.status);
        console.log("Error message:", error.message);
        console.log("Error errors:", error.errors);

        // Handle specific error types
        if (error.status === 422 && error.errors) {
          console.log("Validation errors:", error.errors);
          this.errors = error.errors;
        } else {
          // Show generic error message for other types of errors
          console.log("Non-validation error, showing generic message");
        }
      }
    },

    validateForm() {
      const errors = {};

      if (!this.userData.first_name) {
        errors.first_name = ["First name is required"];
      }
      if (!this.userData.last_name) {
        errors.last_name = ["Last name is required"];
      }
      if (!this.userData.email) {
        errors.email = ["Email is required"];
      }
      if (!this.userData.hotel_name) {
        errors.hotel_name = ["Hotel name is required"];
      }

      // Password validation with immediate feedback
      if (!this.userData.password) {
        errors.password = ["Password is required"];
      } else {
        const passwordErrors = [];
        if (this.userData.password.length < 8) {
          passwordErrors.push("Password must be at least 8 characters");
        }
        if (!/[A-Z]/.test(this.userData.password)) {
          passwordErrors.push(
            "Password must contain at least one uppercase letter"
          );
        }
        if (!/[a-z]/.test(this.userData.password)) {
          passwordErrors.push(
            "Password must contain at least one lowercase letter"
          );
        }
        if (!/\d/.test(this.userData.password)) {
          passwordErrors.push("Password must contain at least one number");
        }
        if (!/[@$!%*?&#]/.test(this.userData.password)) {
          passwordErrors.push(
            "Password must contain at least one special character (@$!%*?&#)"
          );
        }
        if (passwordErrors.length > 0) {
          errors.password = passwordErrors;
        }
      }

      if (!this.userData.password_confirmation) {
        errors.password_confirmation = ["Password confirmation is required"];
      } else if (
        this.userData.password !== this.userData.password_confirmation
      ) {
        errors.password_confirmation = ["Passwords do not match"];
      }

      this.errors = errors;
      return Object.keys(errors).length === 0;
    },

    // Poll the vendor /tabs endpoint until it succeeds or timeout reached
    async waitForVendorReady() {
      const maxAttempts = 20; // ~60 s if interval=3 s
      const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
      for (let i = 0; i < maxAttempts; i++) {
        try {
          await axios.get(`${API.BASE_URL}${API.TABS}`, API.RequestHeaders());
          return true; // success means DB ready
        } catch (err) {
          // 500 likely means still seeding; wait and retry
          await delay(3000);
        }
      }
      throw new Error("Vendor setup timed out");
    },
  },

  created() {
    // Check if user is already authenticated
    if (AuthUtils.isAuthenticated()) {
      AuthUtils.redirectAfterLogin();
    }
  },

  beforeDestroy() {
    // In case component is destroyed before redirect but setup finished
    this.$root.$emit("setup-started", false);
  },
};
</script>

<style scoped>
.register-form {
  width: 100%;
}

.form-title {
  color: #333;
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  text-align: center;
}

.form-subtitle {
  color: #666;
  font-size: 0.9rem;
  text-align: center;
  margin-bottom: 2rem;
}

.alert {
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  border: none;
  font-size: 0.9rem;
}

.alert-danger {
  background-color: #fee;
  color: #c53030;
}

.alert-success {
  background-color: #f0fff4;
  color: #38a169;
}

.alert i {
  margin-right: 0.5rem;
}

.auth-form {
  width: 100%;
}

.form-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.form-row .form-group {
  flex: 1;
  margin-bottom: 0;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  color: #333;
  font-weight: 500;
  font-size: 0.9rem;
  text-align: left;
}

.form-control {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;

  font-size: 1rem;
  transition: all 0.2s ease;
  background-color: #fff;
  padding-right: 75px; /* Make room for both the match indicator and eye icon */
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-control.is-invalid {
  border-color: #e53e3e;
}

.form-control:disabled {
  background-color: #f7fafc;
  cursor: not-allowed;
}

.password-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 8px;
  transition: color 0.2s ease;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
}

.password-toggle:hover {
  color: #333;
}

.password-toggle:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.password-toggle i {
  font-size: 1.1rem;
}

.invalid-feedback {
  color: #e53e3e;
  font-size: 0.85rem;
  margin-top: 0.25rem;
}

.btn {
  padding: 0.875rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-primary {
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #34495e 0%, #2c3e50 100%);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
  background: linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%);
}

.btn-block {
  width: 100%;
}

.loading-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.form-footer {
  text-align: center;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #eee;
}

.signin-link {
  color: #666;
  font-size: 0.9rem;
  margin: 0;
}

.link-primary {
  color: #2c3e50;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.link-primary:hover {
  color: #34495e;
  text-decoration: underline;
}

/* Responsive */
@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
    gap: 0;
  }

  .form-row .form-group {
    margin-bottom: 1.5rem;
  }
}

@media (max-width: 480px) {
  .form-title {
    font-size: 1.3rem;
  }
}

.password-requirements {
  margin-top: 0.75rem;
  font-size: 0.85rem;
  color: #666;
  transition: all 0.5s ease-in-out;
  max-height: 140px;
  opacity: 1;
  overflow: hidden;
  padding-bottom: 0.25rem;
}

.password-requirements.hidden {
  max-height: 0;
  margin-top: 0;
  margin-bottom: 0;
  padding-top: 0;
  padding-bottom: 0;
  opacity: 0;
}

.requirement {
  display: flex;
  align-items: center;
  margin-bottom: 0.25rem;
  transition: all 0.3s ease;
  line-height: 1.4;
}

.requirement.met {
  color: #38a169;
}

.requirement i {
  margin-right: 0.5rem;
  font-size: 0.9rem;
  transition: color 0.2s ease;
}

.requirement.met i {
  color: #38a169;
}

.requirement:not(.met) i {
  color: #a0aec0;
}

.password-match-indicator {
  position: absolute;
  right: 45px; /* Position it to the left of the eye icon */
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #f8f9fa;
  transition: all 0.2s ease;
}

.password-match-indicator i {
  font-size: 0.9rem;
}

.text-success {
  color: #38a169;
}

.text-danger {
  color: #e53e3e;
}

/* Fade transition for the overlay */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.35s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Full-screen overlay */
.setup-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(
    circle at center,
    rgba(255, 255, 255, 0.9) 0%,
    rgba(255, 255, 255, 0.85) 40%,
    rgba(255, 255, 255, 0.8) 70%
  );
  backdrop-filter: blur(6px);
}

/* Card that holds spinner + message */
.setup-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 2.5rem 3rem;
  max-width: 360px;
  width: 90%;
  text-align: center;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 10px 15px -3px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
}

/* Circular loader */
.setup-loader {
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top-color: #2c3e50;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1.5rem;
}

.setup-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 0.5rem;
}

.setup-subtitle {
  font-size: 0.95rem;
  color: #4a5568;
  margin: 0;
}
</style>
